import { InMemoryCache, InMemoryCacheConfig } from '@apollo/client';
import type { Cache, Reference } from '@apollo/client';
import { Metrics } from './Metrics';
import { didRhumbAlreadyFire, watchForRhumbMark } from './rhumbObserver';

const SAMPLE_RATE = 0.2;

const getOperationName = (query: {
  definitions?: ReadonlyArray<{ kind: string; name?: { value: string } }>;
}): string =>
  query.definitions?.find((d) => d.kind === 'OperationDefinition')?.name
    ?.value ?? 'unknown';

type LocationAggregates = {
  writeMs: number;
  diffMs: number;
  flushed: boolean;
  observerSetup: boolean;
};

let aggregatesByLocation: Record<string, LocationAggregates> = {};

const getAggregates = (location: string): LocationAggregates => {
  if (!aggregatesByLocation[location]) {
    const alreadyFired = didRhumbAlreadyFire();

    aggregatesByLocation[location] = {
      writeMs: 0,
      diffMs: 0,
      flushed: alreadyFired,
      observerSetup: alreadyFired,
    };
  }

  return aggregatesByLocation[location];
};

export const __test_only_clearAggregates = () => {
  aggregatesByLocation = {};
};

export class HubspotInMemoryCache extends InMemoryCache {
  private readonly location: string;
  private enabled: boolean | null = null;
  private slowThreshold: number | null = null;

  constructor(config: InMemoryCacheConfig & { location: string }) {
    super(config);
    this.location = config.location;
    this.slowThreshold = this.getSlowThreshold();

    // If the user has set a slow query threshold, enable logging automatically
    if (this.slowThreshold !== null) {
      this.enabled = true;
      this.setupRhumbObserver();
    }
  }

  private getSlowThreshold(): number | null {
    try {
      const stored = localStorage.getItem(
        `apollo_cache_slow_log_threshold_${this.location}`
      );
      if (stored !== null) {
        const parsed = parseFloat(stored);
        return isNaN(parsed) ? null : parsed;
      }
    } catch {
      // localStorage access can throw in some contexts
    }

    return null;
  }

  private timer(name: string, ms: number) {
    Metrics.timer(name, { location: this.location }).update(ms);
  }

  private recordPerOpTimer(
    kind: 'write' | 'diff',
    elapsedMs: number,
    query: Parameters<typeof getOperationName>[0]
  ) {
    const metricName =
      kind === 'write' ? 'cache-write-by-op' : 'cache-diff-by-op';
    Metrics.timer(metricName, {
      location: this.location,
      operation_name: getOperationName(query),
    }).update(elapsedMs);
  }

  private isEnabled(): boolean {
    if (this.enabled === null) {
      this.enabled = Math.random() < SAMPLE_RATE;

      if (this.enabled) {
        this.setupRhumbObserver();
      }
    }

    return this.enabled;
  }

  private flushAggregates() {
    const agg = getAggregates(this.location);
    if (agg.flushed) {
      return;
    }
    agg.flushed = true;

    const combined = agg.writeMs + agg.diffMs;

    this.timer('cache-write-aggregate', agg.writeMs);
    this.timer('cache-diff-aggregate', agg.diffMs);
    this.timer('cache-time-aggregate', combined);

    if (this.slowThreshold !== null) {
      console.log(
        `[${this.location}] [a] ${agg.writeMs.toFixed(
          1
        )}ms write, ${agg.diffMs.toFixed(1)}ms diff, ${combined.toFixed(
          1
        )}ms total`
      );
    }
  }

  private setupRhumbObserver() {
    const agg = getAggregates(this.location);
    if (agg.observerSetup) {
      return;
    }
    agg.observerSetup = true;

    watchForRhumbMark(() => this.flushAggregates());
  }

  /** Instruments the normalization path — StoreWriter.writeToStore() breaks
   *  GraphQL results into flat normalized entities keyed by cache ID. */
  write(options: Cache.WriteOptions): Reference | undefined {
    if (!this.isEnabled()) {
      return super.write(options);
    }

    const start = performance.now();
    const result = super.write(options);
    const elapsed = performance.now() - start;

    this.timer('cache-write', elapsed);
    this.recordPerOpTimer('write', elapsed, options.query);

    if (this.slowThreshold !== null && elapsed >= this.slowThreshold) {
      console.log(
        `[${this.location}] [w] ${getOperationName(
          options.query
        )} (${elapsed.toFixed(1)}ms)`,
        { query: options.query, result: options.result }
      );
    }

    const agg = getAggregates(this.location);
    if (!agg.flushed) {
      agg.writeMs += elapsed;
    }

    return result;
  }

  /** Instruments the read + canonization path. readQuery/readFragment and Apollo's
   *  query watching all delegate to diff(), making it the single chokepoint for
   *  all cache reads. With canonizeResults:true, this includes ObjectCanon.admit(). */
  diff<TData>(options: Cache.DiffOptions<TData>): Cache.DiffResult<TData> {
    if (!this.isEnabled()) {
      return super.diff(options);
    }

    const start = performance.now();
    const result = super.diff(options);
    const elapsed = performance.now() - start;

    this.timer('cache-diff', elapsed);
    this.recordPerOpTimer('diff', elapsed, options.query);

    if (this.slowThreshold !== null && elapsed >= this.slowThreshold) {
      console.log(
        `[${this.location}] [d] ${getOperationName(
          options.query
        )} (${elapsed.toFixed(1)}ms)`,
        { query: options.query, result: result.result }
      );
    }

    const agg = getAggregates(this.location);
    if (!agg.flushed) {
      agg.diffMs += elapsed;
    }

    return result;
  }
}
